import type {
  OsmAdminBoundary,
  OsmAdminLevel,
  OsmBuilding,
  OsmCrossing,
  OsmData,
  OsmLanduseArea,
  OsmNode,
  OsmPlace,
  OsmPlaceKind,
  OsmTreeNode,
  OsmWaterArea,
  OsmWay,
  RoadClass,
  TerrainCover,
} from '@roadworld/shared';

const PLACE_KINDS = new Set<OsmPlaceKind>([
  'city',
  'town',
  'village',
  'suburb',
  'neighbourhood',
  'quarter',
  'hamlet',
  'locality',
]);

interface RawOverpassElement {
  type: 'node' | 'way' | 'relation';
  id: number;
  lat?: number;
  lon?: number;
  nodes?: number[];
  members?: Array<{ type: 'node' | 'way' | 'relation'; ref: number; role: string }>;
  tags?: Record<string, string>;
}

interface RawOverpassResponse {
  elements: RawOverpassElement[];
}

const DRIVABLE: RoadClass[] = [
  'motorway',
  'trunk',
  'primary',
  'secondary',
  'tertiary',
  'residential',
  'service',
  'unclassified',
  'track',
  'motorway_link',
  'trunk_link',
  'primary_link',
  'secondary_link',
  'tertiary_link',
  'living_street',
  'road',
];

const ROAD_CLASSES = new Set<string>([
  ...DRIVABLE,
  'footway',
  'path',
  'cycleway',
  'pedestrian',
  'platform',
  'bridleway',
  'steps',
  'corridor',
]);

const FOREST_TAGS = new Set(['forest', 'wood']);
const SCRUB_TAGS = new Set(['wood', 'scrub']);

function classifyCover(tags: Record<string, string>): TerrainCover | null {
  const landuse = tags.landuse;
  const natural = tags.natural;
  const leisure = tags.leisure;

  if (natural === 'water' || natural === 'wetland') return 'water';
  if (natural === 'bare_rock' || natural === 'scree') return 'rock';
  if (natural === 'sand' || natural === 'beach') return 'sand';
  if (natural === 'wood' || landuse === 'forest' || landuse === 'wood') return 'forest';
  if (natural === 'scrub' || natural === 'heath') return 'scrub';
  if (natural === 'grassland') return 'grass';

  if (landuse === 'farmland' || landuse === 'farmyard' || landuse === 'orchard' || landuse === 'vineyard') {
    return 'farmland';
  }
  if (
    landuse === 'grass' ||
    landuse === 'meadow' ||
    landuse === 'cemetery' ||
    landuse === 'recreation_ground' ||
    landuse === 'village_green'
  ) {
    return 'grass';
  }

  if (leisure === 'park' || leisure === 'garden' || leisure === 'pitch' || leisure === 'golf_course') {
    return 'grass';
  }

  return null;
}

export function parseOverpass(raw: RawOverpassResponse): OsmData {
  const nodes: Record<number, OsmNode> = {};
  const wayLookup = new Map<number, RawOverpassElement>();
  const ways: OsmWay[] = [];
  const buildings: OsmBuilding[] = [];
  const forests: OsmLanduseArea[] = [];
  const landuseAreas: OsmLanduseArea[] = [];
  const water: OsmWaterArea[] = [];
  const trees: OsmTreeNode[] = [];
  const crossings: OsmCrossing[] = [];
  const boundaries: OsmAdminBoundary[] = [];
  const places: OsmPlace[] = [];
  // Track which way ids end up consumed by multipolygon relations, so we don't double-add them.
  const consumedByRelation = new Set<number>();

  // Counter for synthetic node ids used by river-buffered polygons.
  // Use a large negative range so they can't collide with real OSM ids.
  let nextSyntheticNodeId = -1_000_000_000;

  // Pass 1: index nodes and ways.
  for (const el of raw.elements) {
    if (el.type === 'node' && typeof el.lat === 'number' && typeof el.lon === 'number') {
      nodes[el.id] = { id: el.id, lat: el.lat, lng: el.lon };
    } else if (el.type === 'way') {
      wayLookup.set(el.id, el);
    }
  }

  // Pass 2: classify elements.
  for (const el of raw.elements) {
    const tags = el.tags ?? {};

    if (el.type === 'node') {
      const node = nodes[el.id];
      if (!node) continue;
      if (tags.natural === 'tree') {
        trees.push({ id: el.id, lat: node.lat, lng: node.lng });
      }
      if (tags.highway === 'crossing') {
        crossings.push({ id: el.id, lat: node.lat, lng: node.lng, kind: tags.crossing });
      }
      // place=* named point feature — city / suburb / neighbourhood / etc. Only kept if it
      // has a name (anonymous place markers aren't useful for the toast).
      const placeTag = tags.place as OsmPlaceKind | undefined;
      const placeName = tags['name:en'] ?? tags.name;
      if (placeTag && placeName && PLACE_KINDS.has(placeTag)) {
        places.push({
          id: el.id,
          name: placeName,
          kind: placeTag,
          lat: node.lat,
          lng: node.lng,
        });
      }
      continue;
    }

    if (el.type === 'way') {
      if (!el.nodes || el.nodes.length < 2) continue;

      // Road?
      const highway = tags.highway;
      if (highway && ROAD_CLASSES.has(highway)) {
        // Skip footways tagged as sidewalks / crossings / traffic islands — they
        // overlap drivable roads and cause z-fighting + visual noise. Crossings are
        // rendered as decals on the road instead.
        if (
          highway === 'footway' &&
          (tags.footway === 'sidewalk' ||
            tags.footway === 'crossing' ||
            tags.footway === 'traffic_island')
        ) {
          continue;
        }
        const lanesTag = tags.lanes;
        const lanes = lanesTag ? Number.parseInt(lanesTag, 10) : undefined;
        // bridge/tunnel: any value except "no" counts as present (e.g. "yes", "viaduct", "movable").
        const bridgeTag = tags.bridge;
        const bridge = bridgeTag != null && bridgeTag !== 'no' ? true : undefined;
        const tunnelTag = tags.tunnel;
        const tunnel = tunnelTag != null && tunnelTag !== 'no' ? true : undefined;
        const layerTag = tags.layer;
        const layerNum = layerTag != null ? Number.parseInt(layerTag, 10) : NaN;
        const layer = Number.isFinite(layerNum) ? layerNum : undefined;
        ways.push({
          id: el.id,
          nodes: el.nodes,
          roadClass: highway as RoadClass,
          name: tags.name,
          oneway: tags.oneway === 'yes',
          lanes: Number.isFinite(lanes) ? lanes : undefined,
          bridge,
          tunnel,
          layer,
        });
        continue;
      }

      // Building (closed way only — multipolygons are handled below).
      if (tags.building && isClosed(el.nodes)) {
        const b: OsmBuilding = {
          id: el.id,
          outer: el.nodes,
          heightMeters: parseBuildingHeight(tags),
          kind: tags.building,
        };
        if (tags.amenity) b.amenity = tags.amenity;
        if (tags.name) b.name = tags.name;
        buildings.push(b);
        continue;
      }

      // Landuse / natural / leisure area (closed way).
      if (isClosed(el.nodes)) {
        const cover = classifyCover(tags);
        if (cover) {
          const area: OsmLanduseArea = {
            id: el.id,
            outer: el.nodes,
            kind: tags.landuse ?? tags.natural ?? tags.leisure ?? 'unknown',
            cover,
          };
          landuseAreas.push(area);
          // Keep `forests` populated for the tree scatter — same set as before.
          const landuse = tags.landuse;
          const natural = tags.natural;
          if ((landuse && FOREST_TAGS.has(landuse)) || (natural && SCRUB_TAGS.has(natural))) {
            forests.push(area);
          }
        }
      }

      // Water polygons (closed way).
      if (isClosed(el.nodes)) {
        if (tags.natural === 'water') {
          water.push({ id: el.id, outer: el.nodes, kind: 'natural-water' });
          continue;
        }
        if (tags.water === 'river') {
          water.push({ id: el.id, outer: el.nodes, kind: 'river-poly' });
          continue;
        }
      }

      // River LineString — buffer into a polygon ring with synthetic nodes.
      if (tags.waterway === 'river' && !isClosed(el.nodes)) {
        const width = parseRiverWidth(tags);
        const buffered = bufferRiverLineString(el.nodes, nodes, width, () => nextSyntheticNodeId--);
        if (buffered) {
          // Register synthetic nodes into the nodes table so downstream consumers can look them up.
          for (const n of buffered.synthNodes) nodes[n.id] = n;
          water.push({ id: el.id, outer: buffered.ring, kind: 'river-buffered' });
        }
      }
      continue;
    }

    if (el.type === 'relation' && tags.building) {
      // Multipolygon building: outer/inner ways.
      const building = buildMultipolygonBuilding(el, wayLookup);
      if (building) buildings.push(building);
      continue;
    }

    if (el.type === 'relation') {
      // Visual: classify the relation as a landcover area for terrain tinting.
      const cover = classifyCover(tags);
      if (cover) {
        const area = buildMultipolygonLanduse(el, wayLookup, cover, consumedByRelation);
        if (area) landuseAreas.push(area);
      }
      // Water mesh: also push natural=water relations to the dedicated water list so
      // the chunk builder can render them with the PBR water material.
      if (tags.natural === 'water') {
        const poly = buildMultipolygonWater(el, wayLookup);
        if (poly) water.push(poly);
      }
      // Administrative boundary — surface for client-side area-entry detection.
      if (tags.boundary === 'administrative') {
        const adminLevelStr = tags.admin_level;
        const adminLevel = Number.parseInt(adminLevelStr ?? '', 10);
        if (
          adminLevel === 6 ||
          adminLevel === 7 ||
          adminLevel === 8 ||
          adminLevel === 9 ||
          adminLevel === 10
        ) {
          const name = tags['name:en'] ?? tags.name;
          if (name) {
            const b = buildAdminBoundary(el, wayLookup, nodes, name, adminLevel as OsmAdminLevel);
            if (b) boundaries.push(b);
          }
        }
      }
    }
  }

  // Pass 3: stamp amenity tags from standalone amenity nodes onto the building that
  // contains them (point-in-polygon). Lets us reuse the rich `amenity=restaurant` data
  // that OSM authors typically attach to nodes rather than building footprints.
  stampAmenityNodesIntoBuildings(raw.elements, nodes, buildings);

  return {
    nodes,
    ways,
    buildings,
    forests,
    landuseAreas,
    water,
    trees,
    crossings,
    boundaries,
    places,
  };
}

/** Build closed lat/lng rings for an admin-boundary multipolygon relation.
 *
 *  OSM admin boundaries are usually multipolygons of "outer" ways that may individually
 *  be open line strings — they only form a closed ring when stitched together along
 *  shared endpoints. This stitcher walks the outer ways, joining consecutive segments by
 *  matching node ids, and emits one closed ring per connected component. Inner rings
 *  (holes) are ignored — for point-in-polygon membership testing they introduce false
 *  negatives at most, and OSM admin boundaries rarely have meaningful holes.
 *  Returns null if no ring could be closed (sometimes happens when the relation extends
 *  beyond the queried bbox and Overpass drops the truncated segments). */
function buildAdminBoundary(
  rel: RawOverpassElement,
  wayLookup: Map<number, RawOverpassElement>,
  nodes: Record<number, OsmNode>,
  name: string,
  adminLevel: OsmAdminLevel,
): OsmAdminBoundary | null {
  if (!rel.members) return null;
  const outerWays: number[][] = [];
  for (const m of rel.members) {
    if (m.type !== 'way') continue;
    if (m.role !== 'outer' && m.role !== '') continue;
    const way = wayLookup.get(m.ref);
    if (!way?.nodes || way.nodes.length < 2) continue;
    outerWays.push(way.nodes);
  }
  if (outerWays.length === 0) return null;
  const ringsAsIds = stitchRings(outerWays);
  if (ringsAsIds.length === 0) return null;
  const rings: Array<Array<[number, number]>> = [];
  for (const ring of ringsAsIds) {
    const coords: Array<[number, number]> = [];
    for (const nid of ring) {
      const n = nodes[nid];
      if (!n) continue;
      coords.push([n.lng, n.lat]);
    }
    if (coords.length >= 4) rings.push(coords);
  }
  if (rings.length === 0) return null;
  return { id: rel.id, name, adminLevel, rings };
}

/** Stitch unordered open-or-closed line strings into closed rings by matching endpoints.
 *  Returns each closed ring's node-id sequence (first === last). Segments that can't be
 *  closed are dropped. */
function stitchRings(segments: number[][]): number[][] {
  const remaining: number[][] = segments.map((s) => s.slice());
  const rings: number[][] = [];
  while (remaining.length > 0) {
    const seed = remaining.shift()!;
    const ring = seed.slice();
    let progressed = true;
    while (ring[0] !== ring[ring.length - 1] && progressed) {
      progressed = false;
      const tail = ring[ring.length - 1]!;
      for (let i = 0; i < remaining.length; i++) {
        const seg = remaining[i]!;
        const first = seg[0]!;
        const last = seg[seg.length - 1]!;
        if (first === tail) {
          for (let k = 1; k < seg.length; k++) ring.push(seg[k]!);
          remaining.splice(i, 1);
          progressed = true;
          break;
        }
        if (last === tail) {
          for (let k = seg.length - 2; k >= 0; k--) ring.push(seg[k]!);
          remaining.splice(i, 1);
          progressed = true;
          break;
        }
      }
    }
    if (ring[0] === ring[ring.length - 1] && ring.length >= 4) {
      rings.push(ring);
    }
  }
  return rings;
}

function parseRiverWidth(tags: Record<string, string>): number {
  const w = tags.width;
  if (w) {
    const m = Number.parseFloat(w);
    if (Number.isFinite(m) && m > 0.5 && m < 500) return m;
  }
  // Default for waterway=river mapped as a centerline.
  return 10;
}

interface BufferedRiver {
  /** Closed ring of node ids (first === last). */
  ring: number[];
  /** New synthetic nodes that must be added to the OsmData nodes table. */
  synthNodes: OsmNode[];
}

/**
 * Buffer an OSM `waterway=river` LineString into a flat ribbon polygon of the given width.
 *
 * Works in local equirectangular meters around the LineString's centroid, then converts back to
 * lat/lng for the emitted synthetic nodes. Mitre joins at interior vertices, square caps at ends,
 * mitre length clamped to avoid spikes on sharp bends.
 */
function bufferRiverLineString(
  nodeIds: number[],
  nodes: Record<number, OsmNode>,
  width: number,
  nextId: () => number,
): BufferedRiver | null {
  // Resolve to (lat, lng) array, skipping missing nodes.
  const pts: Array<{ lat: number; lng: number }> = [];
  for (const id of nodeIds) {
    const n = nodes[id];
    if (n) pts.push({ lat: n.lat, lng: n.lng });
  }
  if (pts.length < 2) return null;

  // Local equirectangular conversion around centroid.
  let cLat = 0;
  let cLng = 0;
  for (const p of pts) {
    cLat += p.lat;
    cLng += p.lng;
  }
  cLat /= pts.length;
  cLng /= pts.length;
  const mPerDegLat = 110574;
  const mPerDegLng = 111320 * Math.cos((cLat * Math.PI) / 180);
  const toLocal = (p: { lat: number; lng: number }): [number, number] => [
    (p.lng - cLng) * mPerDegLng,
    (p.lat - cLat) * mPerDegLat,
  ];
  const toLatLng = (x: number, y: number): { lat: number; lng: number } => ({
    lat: cLat + y / mPerDegLat,
    lng: cLng + x / mPerDegLng,
  });

  const local: Array<[number, number]> = pts.map(toLocal);

  // Per-segment unit perpendicular (rotate segment direction 90° CCW).
  const segPerp: Array<[number, number]> = [];
  for (let i = 0; i < local.length - 1; i++) {
    const p0 = local[i]!;
    const p1 = local[i + 1]!;
    const dx = p1[0] - p0[0];
    const dy = p1[1] - p0[1];
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return null;
    segPerp.push([-dy / len, dx / len]);
  }

  const half = width / 2;
  const maxMitre = (3 * width) / 2;

  // At each vertex, compute the offset vector (averaged perpendicular for interior, single for ends).
  const offsets: Array<[number, number]> = [];
  for (let i = 0; i < local.length; i++) {
    if (i === 0) {
      const sp = segPerp[0]!;
      offsets.push([sp[0] * half, sp[1] * half]);
    } else if (i === local.length - 1) {
      const sp = segPerp[segPerp.length - 1]!;
      offsets.push([sp[0] * half, sp[1] * half]);
    } else {
      const a = segPerp[i - 1]!;
      const b = segPerp[i]!;
      let ox = (a[0] + b[0]) * half;
      let oy = (a[1] + b[1]) * half;
      const olen = Math.hypot(ox, oy);
      // For a true mitre we'd normalise by dot(bisector, perpendicular); the averaged form is
      // a close approximation for shallow bends and is clamped here for sharp ones.
      if (olen > maxMitre) {
        ox = (ox / olen) * maxMitre;
        oy = (oy / olen) * maxMitre;
      } else if (olen > 0) {
        // Scale up so the offset length is half-width along the bisector.
        const scale = half / Math.max(olen, 1e-6);
        ox *= scale;
        oy *= scale;
        const flen = Math.hypot(ox, oy);
        if (flen > maxMitre) {
          ox = (ox / flen) * maxMitre;
          oy = (oy / flen) * maxMitre;
        }
      }
      offsets.push([ox, oy]);
    }
  }

  // Build the ring: left side forward, then right side reversed.
  const left: Array<[number, number]> = local.map((p, i) => {
    const o = offsets[i]!;
    return [p[0] + o[0], p[1] + o[1]];
  });
  const right: Array<[number, number]> = local.map((p, i) => {
    const o = offsets[i]!;
    return [p[0] - o[0], p[1] - o[1]];
  });

  const ringLocal: Array<[number, number]> = [...left, ...right.slice().reverse()];

  // Emit synthetic nodes + closed ring of ids.
  const synthNodes: OsmNode[] = [];
  const ring: number[] = [];
  for (const [x, y] of ringLocal) {
    const id = nextId();
    const { lat, lng } = toLatLng(x, y);
    const n: OsmNode = { id, lat, lng };
    synthNodes.push(n);
    ring.push(id);
  }
  // Close the ring.
  ring.push(ring[0]!);

  return { ring, synthNodes };
}

function buildMultipolygonWater(
  rel: RawOverpassElement,
  wayLookup: Map<number, RawOverpassElement>,
): OsmWaterArea | null {
  if (!rel.members) return null;
  const outers: number[][] = [];
  const inners: number[][] = [];
  for (const m of rel.members) {
    if (m.type !== 'way') continue;
    const way = wayLookup.get(m.ref);
    if (!way?.nodes || way.nodes.length < 2) continue;
    if (m.role === 'inner') inners.push(way.nodes);
    else outers.push(way.nodes);
  }
  // Accept only already-closed outer ways (prototype, matches building handling).
  const closedOuter = outers.find(isClosed);
  if (!closedOuter) return null;
  const closedInners = inners.filter(isClosed);
  return {
    id: rel.id,
    outer: closedOuter,
    inners: closedInners.length > 0 ? closedInners : undefined,
    kind: 'natural-water',
  };
}

const AMENITY_RX = /^(restaurant|fast_food|cafe|bar|pub|ice_cream)$/;

function stampAmenityNodesIntoBuildings(
  elements: RawOverpassElement[],
  nodes: Record<number, OsmNode>,
  buildings: OsmBuilding[],
): void {
  // Gather candidate amenity nodes once.
  const amenityNodes: Array<{ lat: number; lng: number; amenity: string; name?: string }> = [];
  for (const el of elements) {
    if (el.type !== 'node') continue;
    const a = el.tags?.amenity;
    if (!a || !AMENITY_RX.test(a)) continue;
    const n = nodes[el.id];
    if (!n) continue;
    amenityNodes.push({ lat: n.lat, lng: n.lng, amenity: a, name: el.tags?.name });
  }
  if (amenityNodes.length === 0) return;

  for (const b of buildings) {
    if (b.amenity) continue; // already tagged directly on the building
    // Resolve outer ring to lat/lng polygon.
    const polyLat: number[] = [];
    const polyLng: number[] = [];
    for (const nid of b.outer) {
      const n = nodes[nid];
      if (!n) continue;
      polyLat.push(n.lat);
      polyLng.push(n.lng);
    }
    if (polyLat.length < 3) continue;
    for (const a of amenityNodes) {
      if (pointInPolygon(a.lat, a.lng, polyLat, polyLng)) {
        b.amenity = a.amenity;
        if (!b.name && a.name) b.name = a.name;
        break;
      }
    }
  }
}

/** Ray-casting point-in-polygon. polyLat/polyLng are parallel arrays of the outer ring. */
function pointInPolygon(
  lat: number,
  lng: number,
  polyLat: number[],
  polyLng: number[],
): boolean {
  let inside = false;
  const n = polyLat.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const yi = polyLat[i]!;
    const xi = polyLng[i]!;
    const yj = polyLat[j]!;
    const xj = polyLng[j]!;
    const intersect = (yi > lat) !== (yj > lat)
      && lng < ((xj - xi) * (lat - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function parseBuildingHeight(tags: Record<string, string>): number | undefined {
  const height = tags.height ?? tags['building:height'];
  if (height) {
    const m = Number.parseFloat(height);
    if (Number.isFinite(m) && m > 0) return m;
  }
  const levels = tags['building:levels'] ?? tags.levels;
  if (levels) {
    const n = Number.parseFloat(levels);
    if (Number.isFinite(n) && n > 0) return n * 3.2;
  }
  return undefined;
}

function isClosed(nodes: number[]): boolean {
  return nodes.length >= 4 && nodes[0] === nodes[nodes.length - 1];
}

function buildMultipolygonLanduse(
  rel: RawOverpassElement,
  wayLookup: Map<number, RawOverpassElement>,
  cover: TerrainCover,
  consumedByRelation: Set<number>,
): OsmLanduseArea | null {
  if (!rel.members) return null;
  const outers: number[][] = [];
  const inners: number[][] = [];
  for (const m of rel.members) {
    if (m.type !== 'way') continue;
    const way = wayLookup.get(m.ref);
    if (!way?.nodes || way.nodes.length < 2) continue;
    if (m.role === 'inner') {
      inners.push(way.nodes);
      consumedByRelation.add(way.id);
    } else {
      outers.push(way.nodes);
      consumedByRelation.add(way.id);
    }
  }
  const closedOuter = outers.find(isClosed);
  if (!closedOuter) return null;
  const closedInners = inners.filter(isClosed);
  return {
    id: rel.id,
    outer: closedOuter,
    inners: closedInners.length > 0 ? closedInners : undefined,
    kind: rel.tags?.natural ?? rel.tags?.landuse ?? rel.tags?.leisure ?? 'unknown',
    cover,
  };
}

function buildMultipolygonBuilding(
  rel: RawOverpassElement,
  wayLookup: Map<number, RawOverpassElement>,
): OsmBuilding | null {
  if (!rel.members) return null;
  const outers: number[][] = [];
  const inners: number[][] = [];
  for (const m of rel.members) {
    if (m.type !== 'way') continue;
    const way = wayLookup.get(m.ref);
    if (!way?.nodes || way.nodes.length < 2) continue;
    if (m.role === 'inner') inners.push(way.nodes);
    else outers.push(way.nodes); // treat empty role as outer
  }
  // Stitch open rings. For prototype, accept only already-closed ways.
  const closedOuter = outers.find(isClosed);
  if (!closedOuter) return null;
  const closedInners = inners.filter(isClosed);
  const tags = rel.tags ?? {};
  const b: OsmBuilding = {
    id: rel.id,
    outer: closedOuter,
    inners: closedInners.length > 0 ? closedInners : undefined,
    heightMeters: parseBuildingHeight(tags),
    kind: tags.building,
  };
  if (tags.amenity) b.amenity = tags.amenity;
  if (tags.name) b.name = tags.name;
  return b;
}

export function isDrivable(roadClass: RoadClass): boolean {
  return (DRIVABLE as string[]).includes(roadClass);
}

export function roadWidthMeters(roadClass: RoadClass): number {
  switch (roadClass) {
    case 'motorway':
    case 'trunk':
      return 10;
    case 'primary':
      return 8;
    case 'secondary':
      return 7;
    case 'tertiary':
      return 6;
    case 'motorway_link':
    case 'trunk_link':
      return 6;
    case 'primary_link':
    case 'secondary_link':
    case 'tertiary_link':
      return 5;
    case 'residential':
    case 'unclassified':
    case 'living_street':
    case 'road':
      return 5;
    case 'service':
      return 4;
    case 'track':
      return 3;
    case 'pedestrian':
    case 'platform':
      return 3;
    case 'cycleway':
      return 2;
    case 'bridleway':
      return 2;
    case 'footway':
    case 'path':
      return 1.5;
    case 'steps':
    case 'corridor':
      return 1.5;
    default:
      return 4;
  }
}
